import { Component, OnInit } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import {
    forLifeOf,
    getDynamicRouterLink,
    getRouterLinkById,
    ignoreNullish,
    LifecycleAware
} from '@lsq-frontend/common-ui';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, pluck, startWith } from 'rxjs/operators';
import {
    GetBuyerImplementationCompanyInformationResponse,
    GetBuyerImplementationCompanyInformationService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-buyer-implementation/get-buyer-implementation-company-information';
import { ADMIN_MANAGE_BUYER_COMPANY, atlas } from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { ManageBuyerService } from '../manage-buyer.service';

@Component({
    selector: 'review-buyer-company-information',
    styleUrls: ['./review-buyer-section.scss'],
    template: `
        <buyer-review-section
            [sectionTitle]="'Buyer Information'"
            [editLocation]="editLocationUrl$ | async"
        >
            <div *ngIf="!cachedResponse">
                <lsq-circle-loader></lsq-circle-loader>
            </div>

            <div *ngIf="cachedResponse" [style.padding]="'2rem 3rem'" class="review-buyer-section">
                <div class="row">
                    <div class="col-12 col-lg-6">
                        <p class="label">
                            Buyer Name*
                        </p>
                        <p class="body text-dark-gray" id="companyPreferredName">
                            {{
                                !cachedResponse.companyPreferredName
                                    ? '—'
                                    : cachedResponse.companyPreferredName
                            }}
                        </p>
                    </div>
                    <div class="col-12 col-lg-6">
                        <p class="label">
                            Legal Name*
                        </p>
                        <p class="body text-dark-gray" id="companyLegalName">
                            {{
                                !cachedResponse.companyLegalName
                                    ? '—'
                                    : cachedResponse.companyLegalName
                            }}
                        </p>
                    </div>
                    <div class="col-12 col-lg-6">
                        <p class="label">
                            Buyer External ID*
                        </p>
                        <p class="body text-dark-gray" id="buyerExternalID">
                            {{ !(buyerExternalId | async) ? '—' : (buyerExternalId | async) }}
                        </p>
                    </div>
                    <div class="col-12 col-lg-6">
                        <p class="label">
                            Platform Type*
                        </p>
                        <p class="body text-dark-gray" id="platformName">
                            {{ !cachedResponse.platformName ? '—' : cachedResponse.platformName }}
                        </p>
                    </div>
                    <div class="col-12 col-lg-6">
                        <p class="label">Buyer Organization Type</p>
                        <p class="body text-dark-gray" id="companyOrganizationType">
                            {{
                                !cachedResponse.companyOrganizationType
                                    ? '—'
                                    : cachedResponse.companyOrganizationType
                            }}
                        </p>
                    </div>
                    <div class="col-12 col-lg-6">
                        <p class="label">Buyer Phone Number</p>
                        <p class="body text-dark-gray" id="companyPhoneNumber">
                            {{
                                !cachedResponse.companyPhoneNumber
                                    ? '—'
                                    : (cachedResponse.companyPhoneNumber | phone)
                            }}
                        </p>
                    </div>
                    <div class="col-12 col-lg-6">
                        <p class="label">Buyer Website</p>
                        <p class="body text-dark-gray" id="companyWebsite">
                            {{
                                !cachedResponse.companyWebsite ? '—' : cachedResponse.companyWebsite
                            }}
                        </p>
                    </div>
                    <div class="col-12 col-lg-6">
                        <p class="label">Formation Date</p>
                        <p class="body text-dark-gray" id="formationDate">
                            {{
                                !cachedResponse.formationDate
                                    ? '—'
                                    : (cachedResponse.formationDate | lsqDate: 'shortDate':true)
                            }}
                        </p>
                    </div>
                    <div class="col-12 col-lg-6">
                        <p class="label">Formation State</p>
                        <p class="body text-dark-gray" id="formationState">
                            {{
                                !cachedResponse.formationState ? '—' : cachedResponse.formationState
                            }}
                        </p>
                    </div>
                    <div class="col-12 col-lg-6">
                        <p class="label">Standard Industrial Classification Code</p>
                        <p class="body text-dark-gray" id="sicCode">
                            {{ !cachedResponse.sicCode ? '—' : cachedResponse.sicCode }}
                        </p>
                    </div>
                    <div class="col-12 col-lg-6">
                        <p class="label">Experian BIN</p>
                        <p class="body text-dark-gray" id="experianBin">
                            {{ !cachedResponse.experianBin ? '—' : cachedResponse.experianBin }}
                        </p>
                    </div>
                    <div class="col-12 col-lg-6">
                        <p class="label">Duns Number</p>
                        <p class="body text-dark-gray" id="dunsNumber">
                            {{ !cachedResponse.dunsNumber ? '—' : cachedResponse.dunsNumber }}
                        </p>
                    </div>
                    <div class="col-12 col-lg-6">
                        <p class="label">Employee Identification Number (EIN)</p>
                        <p class="body text-dark-gray" id="ein">
                            {{ !cachedResponse.ein ? '—' : cachedResponse.ein }}
                        </p>
                    </div>
                </div>
            </div>
        </buyer-review-section>
    `
})
@LifecycleAware()
export class ReviewBuyerCompanyInformationComponent implements OnInit {
    private _fetchSubject = new Subject();

    public cachedResponse: GetBuyerImplementationCompanyInformationResponse;

    public editLocationUrl$: Observable<UrlTree>;

    public buyerExternalId = this._manageBuyerService.buyerAccountDetails$.pipe(
        ignoreNullish(),
        pluck('buyerExternalId')
    );

    constructor(
        private _getBuyerImplementationCompanyInformationService: GetBuyerImplementationCompanyInformationService,
        private _manageBuyerService: ManageBuyerService,
        private _router: Router
    ) {}

    public ngOnInit(): void {
        combineLatest([
            this._manageBuyerService.buyerKey$,
            this._fetchSubject.pipe(startWith(true))
        ])
            .pipe(
                forLifeOf(this),
                filter((buyerKey) => !!buyerKey)
            )
            .subscribe(async ([buyerKey, _]) => {
                if (buyerKey) {
                    this.cachedResponse = await this._getBuyerImplementationCompanyInformationService.executeOnce(
                        {
                            buyerImplementationKey: buyerKey
                        }
                    );
                }
            });

        this.editLocationUrl$ = this._manageBuyerService.buyerKey$.pipe(
            map((buyerKey) => {
                const urlTree = this._router.createUrlTree(
                    getDynamicRouterLink(
                        getRouterLinkById(atlas, ADMIN_MANAGE_BUYER_COMPANY, {
                            noStandaloneLeadingSlash: true
                        }),
                        { buyerKey }
                    ),
                    {
                        queryParams: {
                            isImplementation: true
                        }
                    }
                );
                return urlTree;
            })
        );
    }
}
